import React, { useEffect, useState } from 'react';
import { Header, HeaderData } from './components/Header';
import { ScenarioCard, ScenarioData } from './components/ScenarioCard';
import { EstimatedCosts } from './components/CostBreakdown';
import { SelectedSummary } from './components/SelectedSummary';
const INITIAL_HEADER_DATA: HeaderData = {
  title: 'Bare Bones',
  believedValue: 515000,
  datePriced: '2/5/2026',
  creditScore: 622,
  loanType: 'FHA',
  currentRate: '6.250%',
  principalInterest: 3113.51,
  loanAmount: 501762.0,
  mortgageInsurance: 225.0,
  taxesInsurance: 730.71,
  currentTotalPayment: 4069.22
};
const INITIAL_SCENARIOS: ScenarioData[] = [{
  id: 'scenario-1',
  title: 'What They Called About',
  rate: '4.375%',
  accent: 'blue',
  paymentBreakdown: {
    newFirstAmount: 532066.15,
    newFirstPayment: 2656.53,
    mortgageInsurance: 243.86,
    taxesInsurance: 730.71,
    cashNeeded: 0,
    cashToClose: 3631.1
  },
  metrics: {
    monthlySavings: 438.12,
    annualSavings: 5257.42,
    fiveYearSavings: 26287.11,
    thirtyYearSavings: 157722.67
  },
  estimatedCosts: {
    miscBoxAB: 200.0,
    ourFee: 10035.24,
    discountRebate: 15052.86,
    attorneyTitle: 2089.1,
    fundingFee: 2926.95,
    totalCost: 30304.15,
    breakeven: 62.49
  }
}, {
  id: 'scenario-2',
  title: 'What You Want To Do',
  rate: '5.250%',
  accent: 'emerald',
  paymentBreakdown: {
    newFirstAmount: 514429.21,
    newFirstPayment: 2840.7,
    mortgageInsurance: 235.78,
    taxesInsurance: 730.71,
    cashNeeded: 0,
    cashToClose: 3807.15
  },
  metrics: {
    monthlySavings: 262.03,
    annualSavings: 3144.39,
    fiveYearSavings: 15721.97,
    thirtyYearSavings: 94331.81
  },
  estimatedCosts: {
    miscBoxAB: 200.0,
    ourFee: 10035.24,
    discountRebate: -2584.07,
    attorneyTitle: 2089.1,
    fundingFee: 2926.95,
    totalCost: 12667.21,
    breakeven: 37.17
  }
}, {
  id: 'scenario-3',
  title: 'Crazy Idea / Pipe Dream',
  rate: '3.500%',
  accent: 'amber',
  paymentBreakdown: {
    newFirstAmount: 539720.0,
    newFirstPayment: 2423.58,
    mortgageInsurance: 0,
    taxesInsurance: 730.71,
    cashNeeded: 15598.42,
    cashToClose: 3154.29
  },
  metrics: {
    monthlySavings: 914.93,
    annualSavings: 10979.11,
    fiveYearSavings: 54895.56,
    thirtyYearSavings: 329373.36
  },
  estimatedCosts: {
    miscBoxAB: 200.0,
    ourFee: 10035.24,
    discountRebate: 10035.24,
    attorneyTitle: 2089.1,
    fundingFee: null,
    totalCost: 22359.58,
    breakeven: 24.44
  },
  additionalNotes: {
    year2Payment: 3386.0,
    year2Savings: 683.22,
    year3Payment: 3706.0,
    year3Savings: 363.22
  }
}, {
  id: 'scenario-4',
  title: 'Wow, What If',
  rate: '5.750%',
  accent: 'violet',
  paymentBreakdown: {
    newFirstAmount: 505487.81,
    newFirstPayment: 2949.89,
    mortgageInsurance: 231.68,
    taxesInsurance: 730.71,
    cashNeeded: 0,
    cashToClose: 3912.28
  },
  metrics: {
    monthlySavings: 156.94,
    annualSavings: 1883.26,
    fiveYearSavings: 9416.31,
    thirtyYearSavings: 56497.83
  },
  estimatedCosts: {
    miscBoxAB: 200.0,
    ourFee: 10035.24,
    discountRebate: -11525.47,
    attorneyTitle: 2089.1,
    fundingFee: 2926.95,
    totalCost: 3725.81,
    breakeven: 5.09
  }
}];
export function App() {
  const [headerData, setHeaderData] = useState<HeaderData>(INITIAL_HEADER_DATA);
  const [scenarios, setScenarios] = useState<ScenarioData[]>(INITIAL_SCENARIOS);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
  // Scroll to top when a scenario is selected
  useEffect(() => {
    if (selectedScenarioId) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [selectedScenarioId]);
  const handleHeaderChange = (key: keyof HeaderData, value: any) => {
    setHeaderData(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleScenarioChange = (index: number, path: string, value: any) => {
    setScenarios(prev => {
      const newScenarios = [...prev];
      const scenario = {
        ...newScenarios[index]
      };
      // Simple path resolution (e.g., "metrics.monthlySavings" or "title")
      const parts = path.split('.');
      if (parts.length === 1) {
        ;
        (scenario as any)[parts[0]] = value;
      } else if (parts.length === 2) {
        ;
        (scenario as any)[parts[0]] = {
          ...(scenario as any)[parts[0]],
          [parts[1]]: value
        };
      }
      newScenarios[index] = scenario;
      return newScenarios;
    });
  };
  const handleCostChange = (index: number, key: keyof EstimatedCosts, value: number | null) => {
    setScenarios(prev => {
      const newScenarios = [...prev];
      newScenarios[index] = {
        ...newScenarios[index],
        estimatedCosts: {
          ...newScenarios[index].estimatedCosts,
          [key]: value
        }
      };
      return newScenarios;
    });
  };
  const selectedScenario = scenarios.find(s => s.id === selectedScenarioId) || null;
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-900 selection:bg-blue-200" data-id="element-0">
      <Header data={headerData} onChange={handleHeaderChange} data-id="element-1" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-2">
        <SelectedSummary scenario={selectedScenario} currentTotalPayment={headerData.currentTotalPayment} headerData={headerData} onClear={() => setSelectedScenarioId(null)} data-id="element-3" />

        <div className="mb-8 flex justify-between items-end" data-id="element-4">
          <div data-id="element-5">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-6">
              Scenario Comparison
            </h2>
            <p className="text-gray-500 mt-1" data-id="element-7">
              Review your available options and their long-term impact. Click
              any value to edit.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8" data-id="element-8">
          {scenarios.map((scenario, index) => <ScenarioCard key={scenario.id} scenario={scenario} index={index} currentTotalPayment={headerData.currentTotalPayment} isSelected={scenario.id === selectedScenarioId} onSelect={() => setSelectedScenarioId(scenario.id === selectedScenarioId ? null : scenario.id)} onChange={(path, value) => handleScenarioChange(index, path, value)} onCostChange={(key, value) => handleCostChange(index, key, value)} data-id="element-9" />)}
        </div>

        <footer className="mt-16 pt-8 border-t border-gray-200 text-center text-sm text-gray-500" data-id="element-10">
          <p data-id="element-11">
            This is a preliminary estimate. Actual rates, fees, and savings may
            vary based on final underwriting and market conditions.
          </p>
        </footer>
      </main>
    </div>;
}